// Horizontal swipe between the tab roots (Today ↔ Weight ↔ Profile).
// Only active on tab-root routes — sub-pages keep the edge swipe-back, so the
// two gestures never compete. Touches that start inside horizontally
// scrollable elements (date strip, activity tiles) are left alone.
import { useEffect, useRef, type RefObject } from "react";
import { hapticTap } from "./haptics";

const ENGAGE_DX = 20;         // horizontal travel that engages the gesture
const DOMINANCE = 1.4;        // dx must beat dy clearly — don't fight scrolling
const COMMIT_DX = 60;         // travel that commits a tab change
const COMMIT_VELOCITY = 0.35; // px/ms flick that commits
// Content follows the finger with growing resistance, capped — there's no
// neighbouring page to reveal, so a full 1:1 drag would look broken.
const follow = (dx: number) => Math.tanh(dx / 240) * 90;

function insideHorizontalScroller(start: EventTarget | null, stop: HTMLElement): boolean {
  let el = start instanceof Element ? start : null;
  while (el && el !== stop) {
    if (el instanceof HTMLElement && el.scrollWidth > el.clientWidth + 8) {
      const ox = getComputedStyle(el).overflowX;
      if (ox === "auto" || ox === "scroll") return true;
    }
    el = el.parentElement;
  }
  return false;
}

export function useTabSwipe(
  containerRef: RefObject<HTMLElement | null>,
  opts: {
    enabled: boolean;
    /** Index of the current tab in `order`, -1 when not on a tab root. */
    index: number;
    /** Called with the neighbouring tab route on a committed swipe. */
    onNavigate: (to: string) => void;
    order: string[];
  },
) {
  const optsRef = useRef(opts);
  optsRef.current = opts;

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    let startX = 0, startY = 0, lastX = 0, lastT = 0, velo = 0;
    let armed = false, engaged = false, animating = false;

    const clear = () => {
      el.style.transition = "";
      el.style.transform = "";
      el.style.opacity = "";
    };

    const onStart = (e: TouchEvent) => {
      const o = optsRef.current;
      if (animating || !o.enabled || o.index < 0 || e.touches.length !== 1) return;
      if (insideHorizontalScroller(e.target, el)) return;
      armed = true;
      engaged = false;
      velo = 0;
      startX = lastX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      lastT = e.timeStamp;
    };

    const onMove = (e: TouchEvent) => {
      if (!armed) return;
      const t = e.touches[0];
      const dx = t.clientX - startX;
      const dy = t.clientY - startY;
      if (!engaged) {
        if (Math.abs(dx) > ENGAGE_DX && Math.abs(dx) > Math.abs(dy) * DOMINANCE) {
          engaged = true;
          el.style.transition = "none";
        } else if (Math.abs(dy) > 16) {
          armed = false; // vertical scroll — hands off
          return;
        }
      }
      if (engaged) {
        e.preventDefault();
        el.style.transform = `translateX(${follow(dx)}px)`;
        // The tab bar's glass leans with the page (ratio of the capped
        // follow, so it maxes out exactly when the content's drag does).
        window.dispatchEvent(new CustomEvent("caloly:tabswipe", {
          detail: { phase: "move", ratio: follow(dx) / 90 },
        }));
        const dt = e.timeStamp - lastT;
        if (dt > 0) velo = 0.8 * ((t.clientX - lastX) / dt) + 0.2 * velo;
        lastX = t.clientX;
        lastT = e.timeStamp;
      }
    };

    const finish = (e: TouchEvent) => {
      if (!armed) return;
      armed = false;
      if (!engaged) return;
      engaged = false;
      // Committed or not, the gesture is over — the glass goes home or
      // follows the route change, both via its own transition.
      window.dispatchEvent(new CustomEvent("caloly:tabswipe", { detail: { phase: "end" } }));
      const endX = e.changedTouches[0]?.clientX ?? lastX;
      const dx = endX - startX;
      const o = optsRef.current;
      // Swiping left reveals the NEXT tab, right the PREVIOUS one.
      const targetIndex = dx < 0 ? o.index + 1 : o.index - 1;
      const target = o.order[targetIndex];
      const commit =
        !!target && (Math.abs(dx) > COMMIT_DX || (Math.abs(velo) > COMMIT_VELOCITY && Math.abs(dx) > 30));

      if (commit) {
        // Switch NOW — waiting for an exit animation is what feels slow. The
        // incoming page slides in from the swipe direction instead.
        hapticTap();
        o.onNavigate(target!);
        el.style.transition = "none";
        el.style.transform = `translateX(${dx < 0 ? 32 : -32}px)`;
        el.style.opacity = "";
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            el.style.transition = "transform 160ms ease-out";
            el.style.transform = "translateX(0)";
            setTimeout(clear, 200);
          });
        });
      } else {
        animating = true;
        el.style.transition = "transform 140ms ease-out";
        el.style.transform = "translateX(0)";
        setTimeout(() => { clear(); animating = false; }, 160);
      }
    };

    const onCancel = () => {
      armed = false;
      if (engaged) {
        engaged = false;
        clear();
        window.dispatchEvent(new CustomEvent("caloly:tabswipe", { detail: { phase: "end" } }));
      }
    };

    el.addEventListener("touchstart", onStart, { passive: true });
    el.addEventListener("touchmove", onMove, { passive: false });
    el.addEventListener("touchend", finish);
    el.addEventListener("touchcancel", onCancel);
    return () => {
      el.removeEventListener("touchstart", onStart);
      el.removeEventListener("touchmove", onMove);
      el.removeEventListener("touchend", finish);
      el.removeEventListener("touchcancel", onCancel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
